// What a turn made, one list under its reply (Paper "Chat turn · one block", board A1):
// a row each for its docs, a new Area and its routines, with no heading. Each row says what it is
// in its second line ("Doc · …", "New area · Move chats and docs in").
import { ChevronRight } from "lucide-react";
import type { HTMLAttributes, ReactNode } from "react";
import { Link } from "@crew/ui/components/link";
import { cn } from "@crew/ui/lib/utils";

export function MadeList({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      data-slot="made-list"
      className={cn(
        "flex w-full max-w-[520px] flex-col self-start rounded-tile bg-card shadow-ring",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** A row's second line: what it is, and its own link after a dot. */
function MadeDetail({ detail, action }: { detail?: ReactNode; action?: ReactNode }) {
  if (!detail && !action) return null;
  return (
    <span className="flex min-w-0 items-center gap-1 text-xs leading-4 text-muted-foreground">
      {detail ? <span className="min-w-0 truncate">{detail}</span> : null}
      {detail && action ? <span aria-hidden>·</span> : null}
      {action ? <span className="relative z-10 shrink-0">{action}</span> : null}
    </span>
  );
}

/** A row's mark: its icon, or (S-525) a 56×40 tile, an image's thumbnail or a page. */
function MadeMark({ icon, thumb }: { icon: ReactNode; thumb?: ReactNode }) {
  if (thumb) {
    return (
      <span className="flex h-10 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted shadow-ring [&_img]:size-full [&_img]:object-cover [&_img]:object-top">
        {thumb}
      </span>
    );
  }
  return (
    <span className="flex w-5 shrink-0 justify-center text-foreground [&_svg]:size-4">{icon}</span>
  );
}

/**
 * One thing the turn made. The row opens `href`; `action`, a link of its own in
 * the second line (an Area's Move chats and docs in), sits above the row's link.
 */
export function MadeRow({
  icon,
  title,
  detail,
  href,
  action,
  note,
  thumb,
  muted = false,
  ...rest
}: Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  icon: ReactNode;
  /** In place of the icon, a 56×40 tile: a kept page's picture (S-525). */
  thumb?: ReactNode;
  title: ReactNode;
  detail?: ReactNode;
  href: string;
  action?: ReactNode;
  /** A third line, coloured by the caller: a routine's next run (S-525). */
  note?: ReactNode;
  /** A doc since deleted: struck through, and its row links nowhere. */
  muted?: boolean;
}) {
  return (
    <div
      {...rest}
      data-slot="made-row"
      className={cn(
        "relative flex min-h-[52px] items-center gap-2.5 px-3 py-2 md:min-h-12",
        "not-first:before:absolute not-first:before:top-0 not-first:before:right-0 not-first:before:h-px not-first:before:bg-border",
        thumb ? "not-first:before:left-[78px]" : "not-first:before:left-[42px]",
        !muted && "rounded-tile transition-colors hover:bg-foreground/[0.02]",
      )}
    >
      <MadeMark icon={icon} thumb={thumb} />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        {muted ? (
          <span className="truncate text-sm leading-[19px] text-muted-foreground line-through">
            {title}
          </span>
        ) : (
          <Link
            href={href}
            className="truncate text-sm leading-[19px] font-medium text-foreground outline-none after:absolute after:inset-0 after:rounded-tile focus-visible:after:ring-2 focus-visible:after:ring-ring/50"
          >
            {title}
          </Link>
        )}
        <MadeDetail detail={detail} action={action} />
        {note ? <span className="min-w-0 truncate text-xs leading-4">{note}</span> : null}
      </span>
      {muted ? null : (
        <ChevronRight aria-hidden className="size-3.5 shrink-0 text-hint" strokeWidth={2} />
      )}
    </div>
  );
}
